import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { FREIGHT_GET_HELP_REASONS } from '@shipstation/client-common/components/get-help-modal';
import { RAI_CASE_TYPE_TAG_BY_REASON_CODE } from '@shipstation/client-common/components/get-help-modal/freight-get-help-form-urls';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { useOpenFreightGetHelpFormMessages } from './use-open-freight-get-help-form-messages';
import { OpenSierraSource } from '../../../../../../global/support-chat/open-sierra-in-zendesk';
import { useSupportChat } from '../../../../../../global/support-chat/use-support-chat';

import type { GetHelpFieldValues, GetHelpSubmission } from '@shipstation/client-common/components/get-help-modal';
import type { SierraHandoffVariables } from '../../../../../../global/support-chat/open-sierra-in-zendesk';

// Tells Sierra this session came from the freight Get Help modal — set only here, never in the shared WWEX variables bag.
const MESSAGING_ORIGIN = 'freightSupport';

export const useOpenFreightSierraChat = () => {
  const intl = useIntl();
  // isEnabled is intentionally unused: the LaunchDarkly flag alone decides this path.
  const { openSupportChat } = useSupportChat();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openSierraChat = useCallback(
    async (submission: GetHelpSubmission | null, context: GetHelpFieldValues): Promise<boolean> => {
      setIsSubmitting(true);
      try {
        const handoffVariables: SierraHandoffVariables = {
          messagingOrigin: MESSAGING_ORIGIN,
          ...(submission
            ? {
                freightGetHelpReason: FREIGHT_GET_HELP_REASONS[submission.reasonCode].internalName,
                freightGetHelpReasonCode: submission.reasonCode,
                freightGetHelpReasonTag: RAI_CASE_TYPE_TAG_BY_REASON_CODE[submission.reasonCode],
              }
            : {}),
          ...(context.proNumber ? { freightProNumber: context.proNumber } : {}),
          freightBolNumber: context.bolNumber,
        };

        const didOpen = await openSupportChat(OpenSierraSource.FreightGetHelp, handoffVariables);

        if (!didOpen) {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            duration: NOTIFICATION_DURATIONS.MEDIUM,
            title: intl.formatMessage(useOpenFreightGetHelpFormMessages.submitErrorTitle),
          });
        }

        return didOpen;
      } finally {
        setIsSubmitting(false);
      }
    },
    [intl, openSupportChat],
  );

  return { openSierraChat, isSubmitting };
};
